// TelcoTrack — nákup materiálu (dodavatelské ceníky + quote košíku)
// Jediný zdroj nákupních cen materiálu: `material_supplier_prices` (Správa ceníku).
// Katalog rozvětvení (`components`) drží jen zařazení — ne ceny.

import type { MaterialCatalogSupplierOffer } from './material-catalog.constants';

/** Výchozí měna nákupních cen. */
export const MATERIAL_PROCUREMENT_DEFAULT_CURRENCY = 'CZK' as const;

/**
 * Rozhodovací pravidlo v1 (dokumentováno i v docs/infrastructure/material/procurement/):
 * Pro každý řádek košíku vyber aktivní platnou cenu s nejnižší unitPrice,
 * kde quantity >= minQty (nebo minQty chybí). Remíza: název dodavatele (cs), pak id.
 */
export const MATERIAL_PROCUREMENT_DECISION_RULE = 'lowest_unit_price' as const;

export type MaterialProcurementDecisionRule = typeof MATERIAL_PROCUREMENT_DECISION_RULE;

/** Vstupní řádek košíku / quote. */
export interface MaterialProcurementQuoteLineInput {
  sapCode: string;
  quantity: number;
}

/** Kandidát ceny (už vyfiltrovaný na aktivního dodavatele + platnost k asOf). */
export interface MaterialProcurementPriceCandidate {
  priceId: string;
  supplierId: string;
  supplierName: string;
  sapCode: string;
  unitPrice: number;
  currency: string;
  /** Minimální odběr; null/undefined = bez limitu. */
  minQty?: number | null;
  validFrom: string;
  validTo?: string | null;
}

export interface MaterialProcurementQuoteLineResult {
  sapCode: string;
  quantity: number;
  /** Zvolený dodavatel, nebo null když není platná cena. */
  supplierId: string | null;
  supplierName: string | null;
  unitPrice: number | null;
  currency: string | null;
  lineTotal: number | null;
  priceId: string | null;
  /** true = žádný kandidát nesplnil pravidla. */
  unresolved: boolean;
  reason?: string;
}

export interface MaterialProcurementSupplierGroup {
  supplierId: string;
  supplierName: string;
  currency: string;
  lineCount: number;
  subtotal: number;
  lines: MaterialProcurementQuoteLineResult[];
}

export interface MaterialProcurementQuoteResult {
  decisionRule: MaterialProcurementDecisionRule;
  asOf: string;
  lines: MaterialProcurementQuoteLineResult[];
  bySupplier: MaterialProcurementSupplierGroup[];
  unresolvedCount: number;
  /** Součet lineTotal jen u vyřešených řádků (stejná měna — v1 CZK). */
  grandTotal: number;
  currency: string;
}

export interface MaterialSupplierDto {
  id: string;
  name: string;
  active: boolean;
  contactEmail: string | null;
  contactPhone: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MaterialSupplierPriceDto {
  id: string;
  supplierId: string;
  supplierName?: string;
  sapCode: string;
  /** Název produktu/komponenty z katalogu podle SAP. */
  materialName?: string | null;
  unitPrice: number;
  currency: string;
  validFrom: string;
  validTo: string | null;
  minQty: number | null;
  createdAt: string;
  updatedAt: string;
}

/** Výsledek importu měsíční Excel přílohy do `material_supplier_prices`. */
export interface MaterialSupplierPriceImportResult {
  supplierId: string;
  supplierName: string;
  fileName: string;
  validFrom: string;
  validTo: string | null;
  parsedCount: number;
  updatedCount: number;
  unchangedCount: number;
  errorCount: number;
  /** SAP z ceníku, které v `components` nejsou — přeskočeny, katalog se nezakládá. */
  unmatchedCount: number;
  /** Ukázka nepřirazených SAP (max 50). */
  unmatchedSaps: string[];
  sheetsWithHeader: string[];
  sheetsSkipped: string[];
  errors: Array<{ sapCode: string; message: string }>;
}

/**
 * Pure decision: z kandidátů (už platných k asOf) vybere nejlevnějšího dodavatele na řádek
 * a seskupí objednávku podle dodavatele.
 */
export function resolveMaterialProcurementQuote(
  lines: MaterialProcurementQuoteLineInput[],
  candidates: MaterialProcurementPriceCandidate[],
  options?: { asOf?: Date | string; currency?: string },
): MaterialProcurementQuoteResult {
  const asOfDate = options?.asOf ? new Date(options.asOf) : new Date();
  const asOfIso = Number.isNaN(asOfDate.getTime())
    ? new Date().toISOString().slice(0, 10)
    : asOfDate.toISOString().slice(0, 10);
  const currency = (options?.currency || MATERIAL_PROCUREMENT_DEFAULT_CURRENCY).trim() || 'CZK';

  const bySap = new Map<string, MaterialProcurementPriceCandidate[]>();
  for (const c of candidates) {
    const sap = String(c.sapCode ?? '').trim();
    if (!sap || !Number.isFinite(c.unitPrice)) continue;
    const list = bySap.get(sap) ?? [];
    list.push(c);
    bySap.set(sap, list);
  }

  const resolvedLines: MaterialProcurementQuoteLineResult[] = [];
  for (const raw of lines) {
    const sapCode = String(raw?.sapCode ?? '').trim();
    const quantity = Number(raw?.quantity);
    if (!sapCode || !Number.isFinite(quantity) || quantity <= 0) {
      resolvedLines.push({
        sapCode: sapCode || '',
        quantity: Number.isFinite(quantity) ? quantity : 0,
        supplierId: null,
        supplierName: null,
        unitPrice: null,
        currency: null,
        lineTotal: null,
        priceId: null,
        unresolved: true,
        reason: 'Neplatný SAP nebo množství',
      });
      continue;
    }

    const pool = (bySap.get(sapCode) ?? []).filter((c) => {
      const min = c.minQty == null || c.minQty === undefined ? null : Number(c.minQty);
      if (min != null && Number.isFinite(min) && quantity < min) return false;
      return true;
    });

    if (!pool.length) {
      resolvedLines.push({
        sapCode,
        quantity,
        supplierId: null,
        supplierName: null,
        unitPrice: null,
        currency: null,
        lineTotal: null,
        priceId: null,
        unresolved: true,
        reason: 'Žádná platná cena dodavatele',
      });
      continue;
    }

    pool.sort((a, b) => {
      const priceDiff = a.unitPrice - b.unitPrice;
      if (priceDiff !== 0) return priceDiff;
      const nameCmp = String(a.supplierName).localeCompare(String(b.supplierName), 'cs', {
        sensitivity: 'base',
      });
      if (nameCmp !== 0) return nameCmp;
      return String(a.supplierId).localeCompare(String(b.supplierId));
    });

    const best = pool[0];
    const unitPrice = best.unitPrice;
    const lineTotal = roundMoney(unitPrice * quantity);
    resolvedLines.push({
      sapCode,
      quantity,
      supplierId: best.supplierId,
      supplierName: best.supplierName,
      unitPrice,
      currency: best.currency || currency,
      lineTotal,
      priceId: best.priceId,
      unresolved: false,
    });
  }

  const groupMap = new Map<string, MaterialProcurementSupplierGroup>();
  for (const line of resolvedLines) {
    if (line.unresolved || !line.supplierId) continue;
    const key = line.supplierId;
    let group = groupMap.get(key);
    if (!group) {
      group = {
        supplierId: line.supplierId,
        supplierName: line.supplierName || '',
        currency: line.currency || currency,
        lineCount: 0,
        subtotal: 0,
        lines: [],
      };
      groupMap.set(key, group);
    }
    group.lineCount += 1;
    group.subtotal = roundMoney(group.subtotal + (line.lineTotal ?? 0));
    group.lines.push(line);
  }

  const bySupplier = [...groupMap.values()].sort((a, b) =>
    a.supplierName.localeCompare(b.supplierName, 'cs', { sensitivity: 'base' }),
  );

  const unresolvedCount = resolvedLines.filter((l) => l.unresolved).length;
  const grandTotal = roundMoney(
    resolvedLines.reduce((sum, l) => sum + (l.unresolved ? 0 : l.lineTotal ?? 0), 0),
  );

  return {
    decisionRule: MATERIAL_PROCUREMENT_DECISION_RULE,
    asOf: asOfIso,
    lines: resolvedLines,
    bySupplier,
    unresolvedCount,
    grandTotal,
    currency,
  };
}

function roundMoney(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/** Nejlevnější platná jednotková cena pro SAP (po `resolveMaterialProcurementQuote`). */
export interface MaterialCheapestUnitPrice {
  sapCode: string;
  unitPrice: number;
  currency: string;
  supplierId: string;
  supplierName: string;
  priceId: string;
}

function compareSupplierCandidates(
  a: MaterialProcurementPriceCandidate,
  b: MaterialProcurementPriceCandidate,
): number {
  const priceDiff = a.unitPrice - b.unitPrice;
  if (priceDiff !== 0) return priceDiff;
  const nameCmp = String(a.supplierName).localeCompare(String(b.supplierName), 'cs', {
    sensitivity: 'base',
  });
  if (nameCmp !== 0) return nameCmp;
  return String(a.supplierId).localeCompare(String(b.supplierId));
}

/**
 * Všichni dodavatelé s platnou cenou u SAP (1 nabídka / dodavatel = nejnižší unitPrice).
 * Na rozdíl od quote **nefiltruje minQty** — katalog má ukázat každého, kdo SAP nabízí.
 */
export function resolveSupplierPricesBySap(
  sapCodes: readonly string[],
  candidates: MaterialProcurementPriceCandidate[],
  options?: { currency?: string },
): Map<string, MaterialCatalogSupplierOffer[]> {
  const currency = (options?.currency || MATERIAL_PROCUREMENT_DEFAULT_CURRENCY).trim() || 'CZK';
  const unique = [
    ...new Set(sapCodes.map((s) => String(s ?? '').trim()).filter((s) => s.length > 0)),
  ];
  const grouped = new Map<string, Map<string, MaterialProcurementPriceCandidate[]>>();
  for (const c of candidates) {
    const sap = String(c.sapCode ?? '').trim();
    const sid = String(c.supplierId ?? '').trim();
    if (!sap || !sid || !Number.isFinite(c.unitPrice)) continue;
    let bySupplier = grouped.get(sap);
    if (!bySupplier) {
      bySupplier = new Map();
      grouped.set(sap, bySupplier);
    }
    const list = bySupplier.get(sid) ?? [];
    list.push(c);
    bySupplier.set(sid, list);
  }

  const out = new Map<string, MaterialCatalogSupplierOffer[]>();
  for (const sap of unique) {
    const bySupplier = grouped.get(sap);
    if (!bySupplier?.size) continue;
    const offers: MaterialCatalogSupplierOffer[] = [];
    for (const list of bySupplier.values()) {
      const best = [...list].sort(compareSupplierCandidates)[0];
      if (!best) continue;
      offers.push({
        supplierId: best.supplierId,
        supplierName: best.supplierName || '',
        unitPrice: best.unitPrice,
        currency: best.currency || currency,
      });
    }
    offers.sort((a, b) =>
      a.supplierName.localeCompare(b.supplierName, 'cs', { sensitivity: 'base' }),
    );
    if (offers.length) out.set(sap, offers);
  }
  return out;
}

/**
 * Pro každý SAP vybere nejlevnější platnou cenu (stejné pravidlo jako quote s quantity).
 * Kandidáti musí být už vyfiltrovaní na validFrom/validTo k asOf.
 */
export function resolveCheapestMaterialUnitPrices(
  sapCodes: readonly string[],
  candidates: MaterialProcurementPriceCandidate[],
  options?: { asOf?: Date | string; quantity?: number; currency?: string },
): Map<string, MaterialCheapestUnitPrice> {
  const quantity =
    options?.quantity != null && Number.isFinite(options.quantity) && options.quantity > 0
      ? options.quantity
      : 1;
  const unique = [
    ...new Set(
      sapCodes.map((s) => String(s ?? '').trim()).filter((s) => s.length > 0),
    ),
  ];
  const quote = resolveMaterialProcurementQuote(
    unique.map((sapCode) => ({ sapCode, quantity })),
    candidates,
    { asOf: options?.asOf, currency: options?.currency },
  );
  const out = new Map<string, MaterialCheapestUnitPrice>();
  for (const line of quote.lines) {
    if (
      line.unresolved ||
      line.unitPrice == null ||
      !line.supplierId ||
      !line.priceId ||
      !Number.isFinite(line.unitPrice)
    ) {
      continue;
    }
    out.set(line.sapCode, {
      sapCode: line.sapCode,
      unitPrice: line.unitPrice,
      currency: line.currency || MATERIAL_PROCUREMENT_DEFAULT_CURRENCY,
      supplierId: line.supplierId,
      supplierName: line.supplierName || '',
      priceId: line.priceId,
    });
  }
  return out;
}
